/**
 * Progress Tracker — unified progress management for report generation.
 *
 * WHY THIS EXISTS:
 * Browser tabs that are backgrounded (user switches to another tab) throttle
 * JavaScript execution and WebSocket callbacks. This causes three problems:
 *
 *   1. DDP reactive updates stop firing — the server is making progress but
 *      the UI appears frozen because onChange callbacks are deferred.
 *
 *   2. DDP method awaits stall — the server finishes and sends the response,
 *      but the browser doesn't deliver it to the JS thread until the tab is
 *      active again. The UI shows "Preparing download..." indefinitely.
 *
 *   3. Client-side fetch loops (pagination) stall — each await takes seconds
 *      instead of milliseconds because WebSocket is throttled.
 *
 * SOLUTIONS:
 *
 *   createProgressTracker(server) — for DDP subscription-based progress.
 *     - Visibility safety net: force-reads collection on tab focus.
 *     - Stale detection: fires onStale after timeout with no updates.
 *
 *   callWithTabRetry(fn) — for any async call that may stall when backgrounded.
 *     - Races the call against a "tab came back" signal.
 *     - If the tab comes back before the call resolves, retries with a fresh call.
 *     - Works for both DDP method calls and paginated fetch loops.
 *
 * USAGE:
 *
 *   // DDP progress subscription
 *   const tracker = createProgressTracker(server);
 *   await tracker.start(progressId, { onUpdate, onCompleted, onError, onStale });
 *   tracker.cleanup();
 *
 *   // Any async call that may stall when backgrounded
 *   const result = await callWithTabRetry(() => someAsyncCall());
 */

/**
 * Race an async call against a "tab came back" visibility event.
 * If the user switches tabs and comes back before the call resolves,
 * the stalled call is abandoned and retried on the now-active tab.
 */
export function callWithTabRetry(fn) {
  return new Promise((resolve, reject) => {
    let settled = false;

    const attempt = () => {
      let visResolve = null;
      const tabBack = new Promise((r) => { visResolve = r; });

      const onVisible = () => {
        if (document.visibilityState === 'visible' && visResolve) {
          visResolve('tab-back');
          visResolve = null;
        }
      };
      document.addEventListener('visibilitychange', onVisible);
      const cleanupVis = () => document.removeEventListener('visibilitychange', onVisible);

      Promise.race([fn(), tabBack]).then((result) => {
        cleanupVis();
        if (settled) return;
        if (result === 'tab-back') {
          attempt(); // Stalled — retry on active tab
        } else {
          settled = true;
          resolve(result);
        }
      }).catch((err) => {
        cleanupVis();
        if (!settled) {
          settled = true;
          reject(err);
        }
      });
    };

    attempt();
  });
}

/**
 * DDP progress tracker — subscribes to a Meteor 'reportProgress' publication
 * and reactively tracks progress with tab-backgrounding safety nets.
 */
export function createProgressTracker(server) {
  let subscriptionRef = null;
  let reactiveRef = null;
  let visibilityCleanup = null;
  let staleTimer = null;
  let done = false;

  let _staleCallback = null;
  let _staleTimeout = 60000;

  const resetStaleTimer = () => {
    if (staleTimer) clearTimeout(staleTimer);
    if (!done && _staleCallback) {
      staleTimer = setTimeout(() => {
        if (!done) _staleCallback();
      }, _staleTimeout);
    }
  };

  const cleanup = () => {
    if (staleTimer) {
      clearTimeout(staleTimer);
      staleTimer = null;
    }
    if (visibilityCleanup) {
      visibilityCleanup();
      visibilityCleanup = null;
    }
    if (reactiveRef) {
      try { reactiveRef.stop(); } catch (e) {}
      reactiveRef = null;
    }
    if (subscriptionRef) {
      try { subscriptionRef.stop(); } catch (e) {}
      subscriptionRef = null;
    }
  };

  const finish = () => {
    done = true;
    cleanup();
  };

  const start = async (progressId, { onUpdate, onCompleted, onError, onStale, staleTimeout = 60000 }) => {
    cleanup();
    done = false;
    _staleCallback = onStale;
    _staleTimeout = staleTimeout;

    // Create reactive cursor BEFORE subscribing so we don't miss initial data
    const progressCollection = server.collection('report-progress').reactive();
    reactiveRef = progressCollection;

    const processDoc = (doc) => {
      if (!doc || done) return;
      resetStaleTimer();

      if (doc.status === 'error') {
        done = true;
        if (onError) onError(doc);
        return;
      }
      if (doc.status === 'completed') {
        done = true;
        if (onCompleted) onCompleted(doc);
        return;
      }
      onUpdate(doc);
    };

    progressCollection.onChange((newData) => {
      const doc = newData.find(d => d.id === progressId);
      processDoc(doc);
    });

    // On tab focus: force-read the collection to catch up on missed updates
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !done) {
        try {
          const currentData = server.collection('report-progress').fetch();
          const doc = currentData.find(d => d.id === progressId);
          if (doc) processDoc(doc);
        } catch (e) {
          console.warn('[ProgressTracker] Failed to re-read progress on tab focus:', e);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    visibilityCleanup = () => document.removeEventListener('visibilitychange', handleVisibilityChange);

    resetStaleTimer();

    try {
      const progressSub = server.subscribe('reportProgress', progressId);
      await progressSub.ready();
      subscriptionRef = progressSub;
    } catch (e) {
      console.error('[ProgressTracker] Error creating subscription:', e);
    }
  };

  return { start, finish, cleanup };
}
